import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { StatusPillCell, Text } from '@feathr/components';

import { getImportResults } from './DataImportsPage.utils';
import type { IImportDetailsToggle } from './ImportDetailsButton';
import ImportDetailsButton from './ImportDetailsButton';

interface IImportResultsChipProps {
  /** Makes the chip open and close the row's Import details. */
  detailsToggle?: IImportDetailsToggle;
  importer: Importer;
}

/** The Results cell: whether a finished import needs a closer look. */
function ImportResultsChip({
  detailsToggle,
  importer,
}: Readonly<IImportResultsChipProps>): JSX.Element {
  const { t } = useTranslation();
  const results = getImportResults(importer);

  if (!results) {
    return <Text.DefaultRegular>—</Text.DefaultRegular>;
  }

  // Placeholder copy, pending Product and Design.
  const status = results === 'needsReview' ? t('Needs review') : t('No issues');
  const chip = (
    <StatusPillCell
      status={status}
      theme={results === 'needsReview' ? 'warning' : 'success'}
      truncate={false}
    />
  );

  return detailsToggle ? (
    <ImportDetailsButton {...detailsToggle} label={status}>
      {chip}
    </ImportDetailsButton>
  ) : (
    chip
  );
}

export default observer(ImportResultsChip);
